import React from 'react';
import FilterBadge from '@/components/ui/Badge/FilterBadge';
import Button from '@/components/ui/Button';
import { ButtonVariant } from '@/components/ui/Button/ButtonSize';
import { TicketFilters } from '@/types/ticket.types';
import { useGetCompaniesQuery } from '@/components/features/companies/services/companies.queries';
import { useGetDepartmentsByCompanyQuery } from '@/components/features/departments/services/departments.queries';
import { useGetProductsQuery } from '@/components/features/products/services/products.queries';
import { useGetStatusesQuery } from '@/components/features/statuses/services/statuses.queries';
import { useGetPrioritiesQuery } from '@/components/features/priorities/services/priorities.queries';
import { useGetCategoriesQuery } from '@/components/features/categories/services/categories.queries';
import { useGetSectionsQuery } from '@/components/features/sections/services/section.queries';
import { useGetUsersQuery } from '@/components/features/users/services/users.queries';
import useUserStore from '@/stores/useUserStore';

interface ActiveFiltersProps {
    filters: TicketFilters;
    onRemoveFilter: (key: keyof TicketFilters) => void;
    onClearAll: () => void;
}

const MONTH_NAMES = [
    'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
    'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
];

const ActiveFilters: React.FC<ActiveFiltersProps> = ({ filters, onRemoveFilter, onClearAll }) => {
    const { user } = useUserStore();
    const roles = user?.roles || [];
    const isSupervisor = roles.includes('supervisor');

    // Fetch lookup data that all roles can access
    const { data: statuses } = useGetStatusesQuery();
    const { data: priorities } = useGetPrioritiesQuery();
    const { data: categories } = useGetCategoriesQuery();
    const { data: sections } = useGetSectionsQuery();
    
    // Fetch restricted data only if not supervisor (to avoid 403 errors)
    // For supervisors, we skip displaying these filters anyway
    const companiesQuery = useGetCompaniesQuery();
    const departmentsQuery = useGetDepartmentsByCompanyQuery();
    const productsQuery = useGetProductsQuery();
    const usersQuery = useGetUsersQuery({ page_number: 1, page_size: 1000 });
    
    const companies = !isSupervisor ? companiesQuery.data : undefined;
    const departments = !isSupervisor ? departmentsQuery.data : undefined;
    const products = !isSupervisor ? productsQuery.data : undefined;
    const users = !isSupervisor ? usersQuery.data : undefined;

    // Helper function to get display name
    const getDisplayName = (key: keyof TicketFilters, value: string | number): string => {
        switch (key) {
            case 'company_id':
                // For supervisor, show ID if companies data is unavailable
                return companies?.find((c) => c.company_id === value)?.company_name || (isSupervisor ? 'Εταιρία' : value.toString());
            case 'department_id':
                return departments?.find((d) => d.department_id === value)?.department_name || (isSupervisor ? 'Τμήμα' : value.toString());
            case 'product_id':
                return products?.find((p) => p.product_id === value)?.product_name || (isSupervisor ? 'Προϊόν' : value.toString());
            case 'status_id':
                if (Array.isArray(value)) {
                    return value.map(id => statuses?.find((s) => s.status_id === id)?.status_name || id).join(', ');
                }
                return statuses?.find((s) => s.status_id === value)?.status_name || value.toString();
            case 'priority_id':
                return priorities?.find((p) => p.priority_id === value)?.priority_name || value.toString();
            case 'category_id':
                return categories?.find((c) => c.category_id === value)?.category_name || value.toString();
            case 'section_id':
                return sections?.find((s) => s.section_id === value)?.section_name || value.toString();
            case 'user_id': {
                const foundUser = users?.data.find((u) => u.user_id === value);
                return foundUser ? `${foundUser.first_name} ${foundUser.last_name}` : (isSupervisor ? 'Χρήστης' : value.toString());
            }
            case 'month':
                return MONTH_NAMES[Number(value) - 1] || value.toString();
            case 'year':
                return value.toString();
            case 'ticket_number':
                return `#${value}`;
            default:
                return value.toString();
        }
    };

    // Helper function to get label name
    const getFilterLabel = (key: keyof TicketFilters): string => {
        const labels: Record<string, string> = {
            company_id: 'Εταιρία',
            department_id: 'Τμήμα',
            product_id: 'Προϊόν',
            status_id: 'Κατάσταση',
            priority_id: 'Προτεραιότητα',
            category_id: 'Κατηγορία',
            section_id: 'Ενότητα',
            user_id: 'Ανατέθηκε σε',
            month: 'Μήνας',
            year: 'Έτος',
            ticket_number: 'Αριθμός Αιτήματος'
        };
        return labels[String(key)] || String(key);
    };

    // Get active filters (excluding pagination and search)
    const activeFilters = Object.entries(filters).filter(([key, value]) => {
        if (key === 'page_number' || key === 'page_size' || key === 'search' || key === 'ticket_text') {
            return false;
        }
        // Skip filters that supervisors shouldn't see (they don't have access to these endpoints)
        if (isSupervisor && (key === 'company_id' || key === 'department_id' || key === 'product_id' || key === 'user_id')) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
    }) as [keyof TicketFilters, string | number][];

    if (activeFilters.length === 0) {
        return null;
    }

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Ενεργά Φίλτρα:</span>
                    {activeFilters.map(([key, value]) => (
                        <FilterBadge
                            key={key}
                            label={getFilterLabel(key)}
                            value={getDisplayName(key, value)}
                            onRemove={() => onRemoveFilter(key)}
                        />
                    ))}
                </div>
                <Button
                    variant={ButtonVariant.OUTLINE}
                    onClick={onClearAll}
                    className="text-sm"
                >
                    Καθαρισμός Όλων
                </Button>
            </div>
        </div>
    );
};

export default ActiveFilters;
